import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import animationData from '../assets/lottie/naoko-animation.json';
import AuthPopup from '../component/AuthPopup';
// Import the updated component
import ChatPhoneDemoEnhanced from '../component/ChatPreview'; // Adjust path if needed

const HeroSection = ({ onCheckEligibility }) => {
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [phoneScale, setPhoneScale] = useState(1); // State for scale factor
  const chatContainerRef = useRef(null); // Ref for the container

  const ORIGINAL_PHONE_HEIGHT = 812; // Define original height constant
  const ORIGINAL_PHONE_WIDTH = 375; // Define original width

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Effect to calculate and set scale based on container size
  useEffect(() => {
    const calculateScale = () => {
      if (chatContainerRef.current) {
        const containerHeight = chatContainerRef.current.offsetHeight;
        const containerWidth = chatContainerRef.current.offsetWidth;

        // Calculate available space (consider some padding)
        const availableHeight = containerHeight * 0.98; // Use 98% for vertical breathing room
        const availableWidth = containerWidth * 0.98; // Use 98% for horizontal breathing room

        if (containerHeight > 0 && containerWidth > 0) {
            // Calculate scale based on both height and width constraints
            const scaleBasedOnHeight = availableHeight / ORIGINAL_PHONE_HEIGHT;
            const scaleBasedOnWidth = availableWidth / ORIGINAL_PHONE_WIDTH;

            // Use the smaller scale factor to ensure the entire phone fits
            const scale = Math.min(1, scaleBasedOnHeight, scaleBasedOnWidth); // Don't scale up, use smallest scale down factor

            setPhoneScale(scale);
        }
      }
    };

    // Calculate initial scale
    calculateScale();

    // Recalculate on resize using ResizeObserver
    const resizeObserver = new ResizeObserver(entries => {
        // We are observing only one element, so entry[0] is sufficient
        if (entries[0]) {
            calculateScale();
        }
    });

    let currentRef = chatContainerRef.current; // Capture ref value
    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    // Cleanup observer on component unmount
    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount and cleanup runs on unmount


  return (
    <div className="hero-section">
      <div className="hero-container">
        <div className={`hero-content ${isVisible ? 'visible' : ''}`}>
          {/* ... (Badge, Animation, H1, H2, P, Features, Buttons - unchanged) ... */}
           <div className="badge">
            <span>AI-Powered</span>
          </div>

          <div className="animation-container">
            <Lottie animationData={animationData} loop={true} />
          </div>

          <h1>Meet Naoko</h1>
          <h2>Navigate government benefits with confidence</h2>
          <p>Your personal AI assistant that simplifies the complex world of scholarships, welfare programs, and government certificates.</p>

          <div className="features">
            <div className="feature">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span>Personalized eligibility checks</span>
            </div>
            <div className="feature">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>Step-by-step guidance</span>
            </div>
            <div className="feature">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span>Simple document uploads</span>
            </div>
          </div>

          <div className="hero-buttons">
            <button className="primary-button" onClick={onCheckEligibility}>
              Check Your Eligibility
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
            <button className="secondary-button" onClick={() => setShowAuthPopup(true)}>
              Login / Sign Up
            </button>
          </div>
        </div>

        {/* Updated hero-chat section */}
        <div className={`hero-chat ${isVisible ? 'visible' : ''}`}>
          {/* Container for the phone, gets measured */}
          <div className="chat-preview-container" ref={chatContainerRef}>
            {/* Wrapper div to apply scale transform */}
            <div
              className="phone-scale-wrapper"
              style={{
                // Use constants for clarity
                width: `${ORIGINAL_PHONE_WIDTH}px`,
                height: `${ORIGINAL_PHONE_HEIGHT}px`,
                transform: `scale(${phoneScale})`,
                transformOrigin: 'top center',
                transition: 'transform 0.3s ease-out' // Smooth scale transition
              }}
            >
              <ChatPhoneDemoEnhanced />
            </div>
          </div>
        </div>
      </div>

      {showAuthPopup && <AuthPopup onClose={() => setShowAuthPopup(false)} />}

      {/* Styles remain mostly the same, but adjust chat-preview-container */}
      <style jsx>{`
        .hero-section {
          padding: 60px 5%;
          background: linear-gradient(135deg, #f5f7ff 0%, #ffffff 100%);
          min-height: calc(100vh - 120px); /* Adjust based on your header height */
          display: flex;
          align-items: center;
          overflow: hidden; /* Prevent potential overflow issues */
        }

        .hero-container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center; /* Align items vertically centered */
          gap: 40px;
        }

        .hero-content, .hero-chat {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s ease-out;
        }

        .visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Hero Content Styles (Unchanged) --- */
        .badge {
          display: inline-block;
          background-color: #e9efff;
          color: #1a237e;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 24px;
          box-shadow: 0 2px 5px rgba(26, 35, 126, 0.1);
          letter-spacing: 0.5px;
        }
        .hero-content {
          flex: 1;
          min-width: 0;
          max-width: 600px;
        }
        .animation-container {
          width: 160px;
          margin: 0 auto 20px; /* Center if text-align center */
        }
        h1 {
          font-size: 2.8rem;
          font-weight: 800;
          color: #1a237e;
          margin-bottom: 8px;
          line-height: 1.1;
          letter-spacing: -0.5px;
        }
        h2 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #3949ab;
          margin-bottom: 16px;
          line-height: 1.3;
        }
        p {
          font-size: 1rem;
          color: #57597a;
          margin-bottom: 24px;
          line-height: 1.6;
        }
        .features {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 32px;
        }
        .feature {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #3949ab;
          font-weight: 500;
          font-size: 0.95rem;
        }
        .feature svg {
          color: #1a237e;
          flex-shrink: 0;
        }
        .hero-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        .primary-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #1a237e;
          color: white;
          padding: 12px 22px;
          border-radius: 8px;
          border: none;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 14px rgba(26, 35, 126, 0.25);
        }
        .primary-button:hover {
          background: #0d1757;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(26, 35, 126, 0.3);
        }
        .secondary-button {
          background: rgba(255, 255, 255, 0.8);
          color: #1a237e;
          padding: 12px 22px;
          border: 2px solid #1a237e;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s;
          backdrop-filter: blur(5px);
        }
        .secondary-button:hover {
          background: rgba(26, 35, 126, 0.06);
          border-color: #0d1757;
        }
        /* --- End Hero Content Styles --- */


        /* --- Hero Chat Styles (Updated) --- */
        .hero-chat {
          flex: 1;
          max-width: 420px;
          min-width: 300px; /* Minimum width */
          display: flex; /* Use flex to control child */
          flex-direction: column;
          height: 600px; /* Default height, adjust via media queries */
          align-self: center; /* Center itself vertically if container allows */
        }

        .chat-preview-container {
          flex: 1; /* Take full height of .hero-chat */
          overflow: hidden; /* Crucial: Clip the scaled content */
          display: flex;
          justify-content: center; /* Center the scaled wrapper horizontally */
          align-items: flex-start; /* Align scaled item to the top */ /* <<< CORRECTED LINE */
          background: white;
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(26, 35, 126, 0.15);
          padding-top: 15px; /* Optional: Add some padding top if needed */
          padding-bottom: 15px; /* Optional: Add padding bottom for spacing */
          padding-left: 10px;
          padding-right: 10px;
          position: relative; /* For potential absolute positioning inside */
          box-sizing: border-box; /* Include padding in dimensions */
        }

        /* Style for the wrapper that gets scaled */
        .phone-scale-wrapper {
           /* Dimensions set inline based on phone original size */
           /* Transform and origin set inline based on state */
           flex-shrink: 0; /* Prevent shrinking */
        }


        /* --- Media Queries (Adjust .hero-chat height) --- */
         @media (max-width: 1200px) {
          .hero-container {
            gap: 30px;
          }
          h1 { font-size: 2.5rem; }
          .hero-chat { max-width: 380px; height: 550px; } /* Adjusted */
        }

        @media (max-width: 1024px) {
          .hero-section {
            padding: 40px 4%;
            min-height: auto;
          }
          .hero-container {
            flex-direction: column;
            gap: 40px;
            align-items: center; /* Center items horizontally */
          }
          .hero-content {
            max-width: 700px;
            text-align: center; /* Center content */
          }
           .animation-container { margin: 0 auto 20px; } /* Ensure centered */
           .features {
             display: inline-flex;
             flex-direction: column;
             align-items: flex-start; /* Align feature text left */
             margin: 0 auto 32px;
             text-align: left;
           }
           .feature {
             /* Features already flex, align items center is default */
             width: fit-content; /* Allow auto margin centering */
           }
          .hero-buttons {
            justify-content: center;
          }
          .hero-chat {
            width: 100%;
            max-width: 450px; /* Wider allowance */
            height: 500px; /* Adjusted */
             /* Ensure it centers on column layout */
             align-self: center;
             margin-left: auto;
             margin-right: auto;
          }
        }

        @media (max-width: 600px) {
          .hero-section {
            padding: 30px 4%;
          }
          h1 { font-size: 2.2rem; }
          h2 { font-size: 1.2rem; }
          .animation-container { width: 140px; }
          .hero-buttons { flex-direction: column; align-items: center; } /* Stack and center buttons */
          .hero-chat { height: 450px; max-width: 90vw; } /* Use viewport width */
        }

        /* Adjustments for shorter viewports */
        @media (max-height: 800px) and (min-width: 1025px) {
           .hero-section { padding-top: 40px; padding-bottom: 40px; }
           .hero-chat { height: 500px; }
        }
        @media (max-height: 700px) and (min-width: 1025px) {
           .hero-section { padding-top: 30px; padding-bottom: 30px; }
           .hero-chat { height: 450px; }
           h1 { font-size: 2.4rem; }
        }
         @media (max-height: 650px) and (min-width: 1025px) {
           .hero-chat { height: 400px; }
           h1 { font-size: 2.2rem; }
        }

      `}</style>
    </div>
  );
};

export default HeroSection;